import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getChargePlan,
  createChargePlan,
  cancelChargePlan,
  formatMoney,
  type ChargePlan,
} from "../../services/payments";
import { formatDate } from "../../lib/dateFormat";
import DateInput from "../DateInput";

/**
 * Eșalonare — splitting one period into agreed slices.
 *
 * The club picks a number of slices and sees the real dates and the real
 * amounts before committing, rather than doing the division in its head and
 * meeting the remainder afterwards. The parent is notified with those exact
 * dates, because a plan the family does not know about is a plan they will not
 * keep.
 */

export default function ChargePlanDialog({
  chargeId,
  athleteName,
  onClose,
  onChanged,
}: {
  chargeId: string;
  athleteName: string;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const { t } = useTranslation();

  const [plan, setPlan] = useState<ChargePlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(3);
  const [firstDue, setFirstDue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getChargePlan(chargeId);
      setPlan(data);
      if (data.previews.length > 0 && !data.previews.some((p) => p.count === count)) {
        setCount(data.previews[0].count);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || t("plan.loadFailed", "Nu am putut încărca perioada."));
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargeId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const existing = plan?.instalments ?? [];
  const preview = plan?.previews.find((p) => p.count === count) || null;

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      await createChargePlan(chargeId, { count, ...(firstDue ? { firstDueDate: firstDue } : {}) });
      onChanged?.();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || t("plan.createFailed", "Nu am putut crea planul."));
    }
    setBusy(false);
  };

  const cancel = async () => {
    if (
      !window.confirm(
        t("plan.confirmCancel", "Anulezi planul? Perioada redevine o singură plată, cu suma întreagă.")
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await cancelChargePlan(chargeId);
      onChanged?.();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || t("plan.cancelFailed", "Nu am putut anula planul."));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg p-6 bg-white shadow-xl rounded-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-slate-800">{t("plan.title", "Plată în rate")}</h3>
        <p className="mt-1 text-sm text-slate-500">
          {t("plan.hint", "{{name}} — eșalonarea se aplică unei singure perioade.", { name: athleteName })}
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 rounded-full border-indigo-600 animate-spin border-t-transparent" />
          </div>
        ) : !plan ? (
          <p className="py-8 text-sm text-center text-rose-600">{error}</p>
        ) : (
          <>
            <div className="p-3 mt-4 text-sm rounded-xl bg-slate-50">
              <div className="flex justify-between">
                <span className="text-slate-500">{t("plan.period", "Perioada")}</span>
                <span className="font-medium text-slate-800">{plan.charge.period_label}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-500">{t("plan.outstanding", "De încasat")}</span>
                <span className="font-semibold tabular-nums text-slate-800">
                  {formatMoney(plan.charge.outstanding_cents, plan.charge.currency)}
                </span>
              </div>
            </div>

            {existing.length > 0 ? (
              <>
                <p className="mt-5 text-xs font-bold tracking-wide uppercase text-slate-400">
                  {t("plan.current", "Ratele stabilite")}
                </p>
                <div className="mt-2 overflow-hidden border rounded-xl border-slate-200">
                  {existing.map((i) => (
                    <div
                      key={i.id}
                      className="flex items-center justify-between px-3 py-2 text-sm border-b border-slate-100 last:border-0"
                    >
                      <span className="text-slate-700">
                        {t("plan.nth", "Rata {{seq}} din {{count}}", { seq: i.seq, count: i.count })}
                        <span className="block text-[11px] text-slate-400">{formatDate(i.due_date)}</span>
                      </span>
                      <span className="text-right">
                        <span className="block font-semibold tabular-nums text-slate-800">
                          {formatMoney(i.amount_cents)}
                        </span>
                        <span
                          className={`block text-[11px] font-semibold ${
                            i.status === "paid"
                              ? "text-emerald-600"
                              : i.status === "overdue"
                                ? "text-rose-600"
                                : "text-slate-400"
                          }`}
                        >
                          {i.status === "paid"
                            ? t("plan.paid", "achitată")
                            : i.status === "overdue"
                              ? t("plan.late", "restantă")
                              : t("plan.pending", "de achitat")}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>

                {existing.some((i) => i.paid_cents > 0) ? (
                  <p className="mt-3 text-xs text-slate-400">
                    {t(
                      "plan.cannotCancel",
                      "S-a plătit deja o rată, așa că planul nu mai poate fi anulat."
                    )}
                  </p>
                ) : (
                  <button
                    onClick={cancel}
                    disabled={busy}
                    className="mt-3 text-sm font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-50"
                  >
                    {t("plan.cancel", "Anulează planul")}
                  </button>
                )}
              </>
            ) : plan.previews.length === 0 ? (
              <p className="mt-5 text-sm text-slate-500">
                {t("plan.notPossible", "Perioada nu mai poate fi eșalonată — e achitată, anulată sau prea mică.")}
              </p>
            ) : (
              <>
                <label className="block mt-5 mb-2 text-xs font-semibold text-slate-500">
                  {t("plan.count", "În câte rate")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {plan.previews.map((p) => (
                    <button
                      key={p.count}
                      onClick={() => setCount(p.count)}
                      className={`w-10 h-10 text-sm font-bold border rounded-lg transition-colors ${
                        count === p.count
                          ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                          : "border-slate-300 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {p.count}
                    </button>
                  ))}
                </div>

                <label className="block mt-4 mb-1 text-xs font-semibold text-slate-500">
                  {t("plan.firstDue", "Prima rată, scadentă la")}
                </label>
                <DateInput
                  value={firstDue}
                  onChange={setFirstDue}
                  className="w-full px-3 py-2 text-base bg-white border rounded-lg border-slate-300 lg:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  {t("plan.firstDueHint", "Gol = data scadentă a perioadei. Ratele următoare sunt lunare.")}
                </p>

                {preview && (
                  <>
                    <p className="mt-4 text-xs font-bold tracking-wide uppercase text-slate-400">
                      {t("plan.preview", "Cum va arăta")}
                    </p>
                    <div className="mt-2 overflow-hidden border rounded-xl border-slate-200">
                      {preview.amounts.map((amount, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between px-3 py-2 text-sm border-b border-slate-100 last:border-0"
                        >
                          <span className="text-slate-600">
                            {t("plan.nth", "Rata {{seq}} din {{count}}", { seq: idx + 1, count: preview.count })}
                            <span className="block text-[11px] text-slate-400">
                              {firstDue
                                ? t("plan.recalculated", "data se recalculează la salvare")
                                : formatDate(preview.due_dates[idx])}
                            </span>
                          </span>
                          <span className="font-semibold tabular-nums text-slate-800">{formatMoney(amount)}</span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-[11px] text-slate-400">
                      {t(
                        "plan.remainderHint",
                        "Restul împărțirii intră în prima rată, ca familia să nu rămână cu bănuți la final."
                      )}
                    </p>
                  </>
                )}

                <p className="mt-3 text-xs text-slate-500">
                  {t(
                    "plan.noticeHint",
                    "Părinții primesc o notificare cu datele și sumele, iar memento-urile se mută pe datele ratelor."
                  )}
                </p>
              </>
            )}

            {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={onClose}
                disabled={busy}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800 disabled:opacity-50"
              >
                {t("common.close", "Închide")}
              </button>
              {existing.length === 0 && plan.previews.length > 0 && (
                <button
                  onClick={create}
                  disabled={busy}
                  className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {busy ? t("common.loading", "Se încarcă...") : t("plan.create", "Eșalonează")}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
